<template>
  <div class="editable-component" @click="startEditing">
    <div class="editable-component__read-view" v-if="valueToDisplay && !isEditing">
      <span>{{ valueToDisplay }}</span>
      <EditIcon v-if="displayIcon" />
    </div>

    <div class="editable-component__read-view" v-if="!actualValue && !isEditing">
      <span class="editable-component__placeholder"> {{ placeholder }} </span>
      <EditIcon v-if="displayIcon" />
    </div>

    <div class="editable-component__write-view" v-if="isEditing">
      <select
        id="select-type"
        v-if="type === 'select'"
        ref="inputField"
        class="select-type"
        v-model="actualValue"
        @blur="finishEditing"
        @change="finishEditing"
      >
        <option v-for="option of options" :value="option" :key="option">{{ option }}</option>
      </select>

      <input
        v-else
        ref="inputField"
        id="edit-field"
        :type="type"
        :pattern="pattern"
        v-model="actualValue"
        :maxlength="maxLength"
        placeholder="unknown"
        @blur="finishEditing"
        @keypress.enter="finishEditing"
        :max="max"
        :min="min"
      />

      <label for="select-type" class="sr-only">Edit Contact Type value</label>
      <label for="edit-field" class="sr-only">Edit field value</label>
    </div>
  </div>
</template>

<script lang="ts" setup>
import moment from 'moment';
import Toast from '../services/toast';
import { RegexType } from '@/features/bots/models/designer/regexType';
import EditIcon from 'vue-material-design-icons/SquareEditOutline.vue';
import { MAX_INTEGER } from '../constants';
import { PropType, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useVModel } from '@vueuse/core';

const props = defineProps({
  modelValue: {
    type: [String, Number, Date],
    default: '',
  },
  fieldName: {
    type: String,
  },
  type: {
    type: String,
    default: 'text',
  },
  placeholder: {
    type: String,
    default: 'unknown',
  },
  options: {
    type: Array as PropType<string[]>,
  },
  displayRelativeDate: {
    type: Boolean,
    default: true,
  },
  displayIcon: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void;
}>();
const value = useVModel(props, 'modelValue', emit);

const inputField = ref<HTMLInputElement | null>(null);
const pattern = ref('.*');
const maxLength = ref(100);
const max = ref(Infinity);
const min = ref(Infinity);
const actualValue = ref<any>('');
const valueToDisplay = ref<any>('');
const isEditing = ref(false);

const updateDateTimer = ref<number>();

onMounted(() => {
  if (props.type === 'tel') {
    pattern.value = RegexType.PHONE_NUMBER;
    maxLength.value = 14;
  } else if (props.type === 'number') {
    max.value = MAX_INTEGER;
    min.value = -MAX_INTEGER;
  } else if (props.type === 'decimal') {
    pattern.value = RegexType.DECIMAL;
    maxLength.value = 28;
  }
});

onBeforeUnmount(() => clearUpdateTimer());

watch(
  value,
  (newValue: any) => {
    if (!newValue) {
      valueToDisplay.value = '';
      actualValue.value = '';
    } else if (props.type === 'datetime-local') {
      setupDateTime(newValue);
    } else if (props.type === 'date') {
      if (newValue instanceof Date) {
        actualValue.value = moment(newValue).format('YYYY-MM-DD');
        valueToDisplay.value = getDateToDisplay();
        setUpdateDateTimer();
      } else {
        actualValue.value = String(newValue); // Fallback to string
        valueToDisplay.value = String(newValue);
      }
    } else {
      actualValue.value = String(newValue);
      valueToDisplay.value = String(newValue);
    }
  },
  { immediate: true },
);

function startEditing() {
  isEditing.value = true;

  nextTick(() => {
    if (inputField.value) {
      inputField.value.focus();
    }
  });
}

function finishEditing() {
  if (!inputField.value) {
    isEditing.value = false; // Exit editing mode safely
    return;
  }

  if (!inputField.value.checkValidity()) {
    Toast.showError(getErrorMessage());
    actualValue.value = value.value; // Reset to original value
    isEditing.value = false;
    return;
  }

  isEditing.value = false;
  if (props.type === 'datetime-local' || props.type === 'date') {
    value.value = actualValue.value ? new Date(actualValue.value) : null;
  } else if (props.type === 'number') {
    value.value = actualValue.value ? Number(actualValue.value) : null;
  } else {
    value.value = actualValue.value; // Keep as string for text, tel, etc.
  }
}

function setupDateTime(value: any) {
  if (value instanceof Date) {
    valueToDisplay.value = getDateToDisplay();
    setActualValueFromDate(value);
    if (props.displayRelativeDate) {
      setUpdateDateTimer();
    }
  }
}

function setActualValueFromDate(date: Date) {
  const dateAsIso = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
  if (dateHasSeconds(dateAsIso)) {
    actualValue.value = dateAsIso.substring(0, dateAsIso.lastIndexOf(':'));
  } else {
    actualValue.value = dateAsIso.substring(0, dateAsIso.length - 1); // remove zulu time from string
  }
}

function setUpdateDateTimer() {
  clearUpdateTimer();
  updateDateTimer.value = window.setInterval(
    () => (valueToDisplay.value = formatRelativeDate(actualValue.value)),
    30000,
  );
}

function clearUpdateTimer() {
  if (updateDateTimer.value) {
    window.clearInterval(updateDateTimer.value);
  }
}

function dateHasSeconds(value: string): boolean {
  return (value.match(/:/g) || []).length === 2;
}

function getErrorMessage() {
  if (props.fieldName) {
    return `${props.fieldName} is in an invalid format`;
  } else {
    return `${props.type} is in an invalid format`;
  }
}

function getDateToDisplay() {
  if (props.displayRelativeDate) {
    return formatRelativeDate(value.value);
  }
  return formatDate(value.value);
}

function formatRelativeDate(date: Date): string {
  if (!date) {
    return '';
  }
  return moment(date).fromNow();
}

function formatDate(date: Date) {
  return moment(date).format('DD/MM/YYYY HH:mm');
}
</script>

<style scoped lang="scss">
.editable-component {
  height: 22px;

  &__read-view {
    border: 1px solid transparent;
    box-shadow:
      inset 0 1px 2px transparent,
      0 0 5px transparent;
    border-radius: 4px;
    padding-left: 4px;
    display: flex;
    gap: 1rem;
    .material-design-icon {
      color: #363636;
      opacity: 0.6;
      cursor: pointer;
      &__svg {
        bottom: 0;
      }
    }
  }

  &__write-view {
    line-height: inherit;
    padding-left: 4px;
    border: none;
    background-color: transparent;
    font-size: inherit;
    color: inherit;

    input {
      width: 100%;
    }

    input:focus,
    select:focus {
      outline: none;

      border: 1px solid #3273dc;
      box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
      border-radius: 4px;
    }
  }

  &__placeholder {
    opacity: 0.6;
  }
}

input {
  border: none;
  outline: none;
}
</style>
